import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  Stack,
} from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';

import { popoverShadow } from 'theme/shadows';
import { IListColumn } from '../columns';

interface IColumnMenuProps<Id extends string> {
  columns: IListColumn<Id, any>[];
  selected: Id[];
  onChange: (columns: Id[]) => void;
}

export const ColumnMenu = <Id extends string>({
  columns,
  selected,
  onChange,
}: IColumnMenuProps<Id>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const toggle = (id: Id) =>
    onChange(
      columns
        .filter((column) =>
          column.id === id
            ? !selected.includes(id)
            : selected.includes(column.id),
        )
        .map((column) => column.id),
    );

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<ViewColumnOutlinedIcon sx={{ width: 16, height: 16 }} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          minHeight: '2.5rem',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
          textTransform: 'none',
        }}
      >
        Manage columns
      </Button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'background.divider',
              boxShadow: popoverShadow,
            },
          },
        }}
      >
        <Stack>
          {columns.map((column) => (
            <FormControlLabel
              key={column.id}
              sx={{
                '&.Mui-disabled': { cursor: 'not-allowed' },
                '& .MuiFormControlLabel-label.Mui-disabled': {
                  color: 'text.secondary',
                },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(column.id)}
                  disabled={column.alwaysOn}
                  onChange={() => toggle(column.id)}
                  sx={{
                    '&.Mui-checked.Mui-disabled': {
                      color: 'background.dividerStrong',
                    },
                  }}
                />
              }
              label={column.label}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
};
